// ── law_focus_pull — netti goes out of focus, it is never frosted over ──────
//
// netti used to step back behind words under a FROST: a copy of it, blurred by
// a backdrop-filter inside a radial mask, cross-faded over the sharp creature by
// the reading ramp. Half-way through that fade you see both at once, the sharp
// petals and a blurred ghost of them, which reads as a wet, gritty smear; and
// the mask's ellipse left the petal tips outside it perfectly sharp while the
// middle was soft, so the blur seemed to come from several directions.
//
// A camera does it differently, and so do we now: the creature itself is
// defocused, uniformly, by ONE `filter: blur()` on its own layer (see
// .ob-focus in components/organism-focus.css). One value, 0..1, says how far
// out of focus it is:
//
//   · the SCROLL ramp takes it out quickly: fully soft a little past half of a
//     surface's own ramp (FOCUS_REACH), and it simply stays there however far
//     you read; scrolling back up brings it in over the same short stretch, and
//     at the top it is sharp again.
//   · a FLOOR says how far out of focus a place is before anyone scrolls: the
//     navigator's outer rooms (your projects, a project's own page) keep netti
//     sharp; deeper in (a folder, a document, settings) it rests in the
//     background already, where it costs least (the calm bus drops its frame
//     rate and resolution, which a defocused creature never shows).
//
// The value is quantised (FOCUS_STEPS) and eased by one short CSS transition,
// so a scroll writes at most ~10 distinct radii across the whole pull and the
// compositor animates between them; nothing here runs per frame.

export const FOCUS_REACH = 0.62;
export const FOCUS_STEPS = 10;

const ramps: Record<string, number> = {};
let floor = 0;
let written = -1;

/** 0..1 → the quantised focus a ramp asks for (fully soft at FOCUS_REACH). */
export function focusFromRamp(ramp: number): number {
  const f = Math.max(0, Math.min(1, ramp / FOCUS_REACH));
  return Math.round(f * FOCUS_STEPS) / FOCUS_STEPS;
}

function write() {
  if (typeof document === "undefined") return;
  let f = floor;
  for (const k in ramps) f = Math.max(f, ramps[k]);
  f = Math.round(Math.max(0, Math.min(1, f)) * FOCUS_STEPS) / FOCUS_STEPS;
  if (f === written) return;
  written = f;
  const root = document.documentElement;
  root.style.setProperty("--netti-focus", String(f));
  // the filter exists only while netti is out of focus: a blur(0) is still an
  // offscreen pass, so a sharp netti carries no filter at all.
  root.toggleAttribute("data-netti-focus", f > 0);
}

/** one surface's scroll ramp (0..1 over that surface's own reach). */
export function setFocusRamp(prefix: string, ramp: number): void {
  ramps[prefix] = focusFromRamp(ramp);
  write();
}

export function clearFocusRamp(prefix: string): void {
  delete ramps[prefix];
  write();
}

/** how far out of focus this place is before any scroll (0 sharp · 1 soft). */
export function setFocusFloor(v: number): void {
  floor = Math.max(0, Math.min(1, v));
  write();
}

/** the level netti should rest at for what it is showing (the calm bus). */
export function currentFocus(): number {
  return Math.max(0, written);
}
